import { useMemo, useState, type ReactNode } from "react";
import { ActivityIndicator, Text, View } from "react-native";
import { useTranslation } from "react-i18next";

import { DisclosureCard } from "../primitives/DisclosureCard";
import { PeriodSelector } from "./PeriodSelector";
import { DEFAULT_WINDOW_DAYS, rhythmBarCount } from "./progressWindow";
import type { UiTheme } from "../theme";
import { MuscleCoverageSection } from "./MuscleCoverageSection";
import { buildMuscleCoverage } from "./muscleCoverage";
import { useSessionResultsFromDeps } from "./useSessionResults";
import { E1RMTrendsPanel } from "./E1RMTrendsPanel";
import { ExerciseFrequencyPanel } from "./ExerciseFrequencyPanel";
import { FatigueDriftPanel } from "./FatigueDriftPanel";
import { IntensityHeatmapPanel } from "./IntensityHeatmapPanel";
import { MovementBalancePanel } from "./MovementBalancePanel";
import { PainVolumePanel } from "./PainVolumePanel";
import { RepsAtLoadTrendsPanel } from "./RepsAtLoadTrendsPanel";
import type { SessionSummary } from "./progressMath";
import { makeProgressViewStyles } from "./progressViewStyles";
import type { ProgressDeps } from "./types";
import { useBreakpoint } from "../useBreakpoint";

// THE Progress surface — one composed tree (the coverage census +
// supporting panels → Tier-2 metrics → session-count stats + weekly-frequency chart →
// records) for mobile's Progress tab AND the web-rnw console. The
// data-heavy panels self-fetch through deps.apiFetch; the host owns the
// scroll container (mobile adds pull-to-refresh) and passes the session
// summary it already fetches. The volume-by-tag records card is injected
// as a slot because it needs host-specific data wiring (chartWorkouts +
// tag localization), not because it's native-only — both hosts render the
// SAME shared `ProgressRecordsCard` (lift/progress-tag-volume-web).
export function ProgressView({
  deps,
  summary,
  weeklyCounts,
  summaryError,
  formatDate,
  locale,
  recordsSlot,
}: {
  deps: ProgressDeps;
  summary: SessionSummary | null;
  weeklyCounts: number[];
  summaryError: string | null;
  /** Format the last-session date in the host's preferred style. */
  formatDate: (iso: string, locale: string) => string;
  locale: string;
  /** Volume-by-tag mini-charts — the shared `ProgressRecordsCard`, host-fed. */
  recordsSlot: ReactNode;
}) {
  const { theme } = deps;
  const { t } = useTranslation();
  const { wide } = useBreakpoint();
  const styles = makeProgressViewStyles(theme);

  // PG-1 period selector — the analysis window driving Strength, Rhythm and the
  // "More detail" panels. State lives here (a display-only UI concern, no
  // host-specific data), so neither host changes: ProgressView injects it into
  // the panel deps and re-buckets the rhythm chart from the raw weekly counts.
  const [windowDays, setWindowDays] = useState(DEFAULT_WINDOW_DAYS);
  const panelDeps = useMemo(() => ({ ...deps, windowDays }), [deps, windowDays]);

  // Rhythm chart follows the window: show the trailing ⌈window/7⌉ weekly bars
  // out of the host's raw history (newest last), min 1. Coverage stays weekly.
  const rhythmCounts = useMemo(
    () => weeklyCounts.slice(-rhythmBarCount(windowDays)),
    [weeklyCounts, windowDays],
  );
  const maxWeekly = Math.max(1, ...rhythmCounts);

  // PG-1 muscle-coverage hero — the per-muscle weekly census, surfaced at the
  // top of Progress (the metric Antenore said was missing). Reads the SAME
  // single-flight session-results the panels use (no extra fetch); scores
  // against the pinned program when the host wires it, else degraded. Fixed
  // weekly cadence by design (targets are weekly).
  // `error` is read, not dropped: the coverage hero is the first thing on
  // this screen now, so a failed read has to say so instead of rendering as
  // an absence (PG-8).
  const { results, catalog, error } = useSessionResultsFromDeps(deps);
  const coverage = useMemo(
    () =>
      results && catalog
        ? buildMuscleCoverage(
            results,
            catalog,
            deps.pinnedProgram ?? null,
            deps.prescription ?? null,
            Date.now(),
            deps.weekStartDay,
            deps.athleteTargets ?? null,
          )
        : null,
    [
      results,
      catalog,
      deps.pinnedProgram,
      deps.prescription,
      deps.weekStartDay,
      deps.athleteTargets,
    ],
  );

  // PG-1 §3 / measurement-contract §13.1 — the remaining deep-cut analytical
  // panels (fatigue, intensity, per-exercise frequency) fold behind the "More
  // detail" disclosure so they stop being a wall above the glanceable surface.
  // Movement balance and pain were PROMOTED out of the fold (mounted directly
  // above, next to E1RM — they say something true with 1-2 sessions); density
  // was retired from the athlete's Progress surface entirely (its trend arrow
  // was an ungated two-point delta). DensityTrendPanel still lives as the Coach
  // AthleteDetail "volume" panel, so the component/math/i18n are NOT deleted.
  // Listed ONCE here — the grid-vs-column choice wraps the same nodes, so the
  // wide 2-col layout no longer duplicates the panel list. On a phone / narrow
  // browser they stack in one column; on a desktop-class viewport they fill the
  // 2-column grid. Mobile is always narrow → never the grid branch.
  const detailPanels: Array<[string, ReactNode]> = [
    ["fatigue", <FatigueDriftPanel {...panelDeps} />],
    ["intensity", <IntensityHeatmapPanel {...panelDeps} />],
    ["frequency", <ExerciseFrequencyPanel {...panelDeps} />],
  ];
  const detailPanelGroup = wide ? (
    <View style={styles.grid}>
      {detailPanels.map(([k, node]) => (
        <View key={k} style={styles.gridCell}>
          {node}
        </View>
      ))}
    </View>
  ) : (
    detailPanels.map(([k, node]) => (
      <View key={k}>{node}</View>
    ))
  );

  return (
    <>
      {/* PG-8 — the census IS the panel. What used to sit above it was a
          second opinion about the same athlete, in a quantity that could not
          be compared with this one, and it printed "Nothing to flag" over a
          card counting what to flag. */}
      {/* PG-1 hero — "am I hitting my weekly muscle targets". */}
      <MuscleCoverageSection
        theme={theme}
        coverage={coverage}
        units={deps.units}
        locale={locale}
        figure={deps.figure}
        isDark={deps.isDark}
        loading={results === null || catalog === null}
        error={error}
      />
      {/* PG-1 period selector — drives Strength, Rhythm and the "More detail"
          panels below. Sits atop the trend block (Coverage above it stays
          weekly and is unaffected). */}
      <PeriodSelector theme={theme} value={windowDays} onChange={setWindowDays} />
      {/* Strength — "are my top lifts progressing" — stays visible under the
          coverage hero (Antenore's glanceable surface). */}
      <E1RMTrendsPanel {...panelDeps} />
      {/* The second strength axis (measurement-contract §8): reps at a fixed
          load over time — the gain the e1RM chart can't see on high-rep and
          machine work. Sits in the same strength zone as its sibling; fetches
          its own raw-set window (the session-results cache keeps only top
          sets), so unlike the panels below it does add one window fetch. */}
      <RepsAtLoadTrendsPanel {...panelDeps} />
      {/* Promoted out of the fold (measurement-contract §13.1): both say
          something true with 1-2 sessions — movement balance is a literal
          windowed sum per pattern, pain × volume is the safety signal. Same
          self-fetching panels, same single-flight session-results cache the
          panels above already share (no extra fetch from mounting earlier). */}
      <MovementBalancePanel {...panelDeps} />
      <PainVolumePanel {...panelDeps} />
      {summary === null && summaryError === null ? (
        <ActivityIndicator color={theme.palette.ink2} />
      ) : summaryError !== null ? (
        <Text style={[styles.statValue, { color: theme.palette.danger }]}>
          {summaryError}
        </Text>
      ) : summary !== null ? (
        <>
          <View style={styles.statGrid}>
            <Stat
              label={t("mobile.progress.windowAll")}
              value={String(summary.total)}
              styles={styles}
            />
            <Stat
              label={t("mobile.progress.window30d")}
              value={String(summary.last30d)}
              styles={styles}
            />
            <Stat
              label={t("mobile.progress.window7d")}
              value={String(summary.last7d)}
              styles={styles}
            />
            <Stat
              label={t("mobile.progress.lastSession")}
              value={
                summary.lastSession
                  ? formatDate(summary.lastSession.startedAt, locale)
                  : "—"
              }
              styles={styles}
            />
          </View>

          <View style={styles.card}>
            <Text style={styles.cardTitle}>
              {t("mobile.progress.frequencyTitle")}
            </Text>
            <Text style={styles.cardHelp}>
              {t("mobile.progress.frequencyHelp", { weeks: rhythmCounts.length })}
            </Text>
            <View style={styles.barChart}>
              {rhythmCounts.map((c, idx) => {
                const heightPct = (c / maxWeekly) * 100;
                return (
                  <View key={idx} style={styles.barColumn}>
                    <View
                      style={[
                        styles.bar,
                        {
                          height: `${Math.max(2, heightPct)}%`,
                          backgroundColor:
                            idx === rhythmCounts.length - 1
                              ? theme.palette.brand500
                              : theme.palette.ink3,
                        },
                      ]}
                    />
                    <Text style={styles.barLabel}>{c > 0 ? c : ""}</Text>
                  </View>
                );
              })}
            </View>
          </View>
        </>
      ) : null}
      {/* PG-1 §3 — the remaining analytical panels (fatigue, intensity,
          per-exercise frequency) + the volume-by-tag records card fold behind
          "More detail". Collapsed by default; the two self-fetching panels
          inside (fatigue, intensity) only hit the network once the reader
          opens the fold. */}
      <DetailsSection theme={theme}>
        {detailPanelGroup}
        {recordsSlot}
      </DetailsSection>
    </>
  );
}

function Stat({
  label,
  value,
  styles,
}: {
  label: string;
  value: string;
  styles: ReturnType<typeof makeProgressViewStyles>;
}) {
  return (
    <View style={styles.stat}>
      <Text style={styles.statLabel}>{label}</Text>
      <Text style={styles.statValue}>{value}</Text>
    </View>
  );
}

// PG-1 §3 — the "More detail" fold: the deep-cut analytical panels + records
// card behind a collapsed-by-default disclosure. The box/header/chevron/divided
// body is the shared <DisclosureCard>; here we own only the title/subtitle text
// and the body spacing. The a11y label folds in the subtitle so a screen-reader
// user hears what the sighted subtitle shows (the header text is otherwise
// silent once accessibilityLabel is set — mirrors WeeklyTargetsCard).
function DetailsSection({
  theme,
  children,
}: {
  theme: UiTheme;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const styles = makeProgressViewStyles(theme);
  const title = t("progress.detailsTitle", { defaultValue: "More detail" });
  const subtitle = t("progress.detailsSubtitle", {
    defaultValue: "Deeper training analytics",
  });
  return (
    <DisclosureCard
      theme={theme}
      accessibilityLabel={[title, subtitle].filter(Boolean).join(". ")}
      bodyStyle={styles.detailsBody}
      header={
        <>
          <Text style={styles.detailsEyebrow}>{title}</Text>
          <Text style={styles.detailsSub}>{subtitle}</Text>
        </>
      }
    >
      {children}
    </DisclosureCard>
  );
}
